import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Connects to data-controller="filter-params"
//
// The form submits a bare GET /configurator, which by itself discards any
// product_id / variant_id the user set in the URL (e.g. via the variant
// selector's history.replaceState). Before submitting, pull the live values
// off the current URL and merge them with the form fields so the resulting
// navigation preserves every param in window.location, only overriding the
// ones the form is responsible for.
export default class extends Controller {
  submit() {
    this.visitWithMergedParams();
  }

  debouncedSubmit() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.visitWithMergedParams(), 300);
  }

  submitOnEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.visitWithMergedParams();
    }
  }

  disconnect() {
    clearTimeout(this.timeout);
  }

  visitWithMergedParams() {
    const action = this.element.getAttribute("action") || window.location.pathname;
    const target = new URL(action, window.location.origin);

    // Seed the target with every live URL param so nothing the user set
    // outside this form (product_id, variant_id, …) gets dropped. Use
    // append so multi-value params (foo[]=1&foo[]=2) survive.
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      target.searchParams.append(key, value);
    });

    // Now let the form's own inputs win for the keys they own. Collect
    // owned keys from the DOM, not from FormData — unchecked checkboxes
    // and unselected radios don't appear in FormData, so relying on it
    // would leave their stale values in the URL after the user clears
    // every box in a group. Read the attribute (not the property) because
    // custom elements like <el-select> don't auto-reflect `name` as a JS
    // property.
    const ownedKeys = new Set();
    this.element.querySelectorAll("[name]").forEach((el) => {
      const key = el.getAttribute("name");
      if (!key || key === "authenticity_token" || key === "utf8") return;
      ownedKeys.add(key);
    });
    ownedKeys.forEach((key) => target.searchParams.delete(key));

    // Append every non-empty form value — append (not set) so checkbox
    // arrays like product_ids[] keep every checked entry instead of
    // overwriting down to the last one.
    new FormData(this.element).forEach((value, key) => {
      if (!ownedKeys.has(key)) return;
      const str = typeof value === "string" ? value : "";
      if (str.length > 0) target.searchParams.append(key, str);
    });

    // Form-associated custom elements (e.g. <el-select>) don't always show
    // up in FormData at the moment `change` fires. For any owned key still
    // missing from the URL, fall back to reading the value attribute off
    // non-native [name] elements directly. Skip native form fields so
    // unchecked checkboxes / unselected radios stay absent (their value
    // attribute would re-add them).
    this.element.querySelectorAll("[name]").forEach((el) => {
      const key = el.getAttribute("name");
      if (!ownedKeys.has(key)) return;
      if (target.searchParams.has(key)) return;
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement || el instanceof HTMLButtonElement) return;
      const value = el.getAttribute("value");
      if (typeof value === "string" && value.length > 0) {
        target.searchParams.append(key, value);
      }
    });

    // Honor data-turbo-frame on the form so the response replaces only that
    // frame instead of the whole page (avoids flickering the sidebar that
    // hosts this form).
    const frameId = this.element.dataset.turboFrame;
    const visitOptions = { action: "advance" };
    if (frameId) visitOptions.frame = frameId;
    Turbo.visit(target.toString(), visitOptions);
  }
}
